import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Form, Button, Container, Row, Col, Alert } from 'react-bootstrap'
import { formatErrorResponse } from '../utils/errorHandlers'
import { brandAPI } from '../api/brandAPI'
import { usePermissions } from '../contexts/PermissionContext'

function BrandUpdateScreen() {
    const { hasPermission, loading: permissionsLoading } = usePermissions()
    const { id } = useParams()
    const navigate = useNavigate()
    const [error, setError] = useState(null)
    const [brand, setBrand] = useState({
        name: '',
        is_active: true
    })

    useEffect(() => {
        async function fetchBrand() {
            try {
                const data = await brandAPI.getById(id);
                setBrand(data)
            } catch (error) {
                console.error('Error fetching brand:', error)
                setError('Failed to load brand data')
            }
        }
        fetchBrand()
    }, [id])

    const submitHandler = async (e) => {
        e.preventDefault()
        try {
            await brandAPI.update(id, brand);
            navigate('/brands')
        } catch (error) {
            setError(formatErrorResponse(error))
        }
    }

    const handleToggleActive = async (e) => {
        e.preventDefault();

        if (brand.is_active) {
            if (!window.confirm('Are you sure you want to deactivate this brand?')) {
                return;
            }
        }

        try {
            const updatedBrand = {
                ...brand,
                is_active: !brand.is_active
            };

            await brandAPI.update(id, updatedBrand);
            setBrand(updatedBrand);
        } catch (error) {
            console.error('Error toggling brand status:', error);
            alert(`Failed to update brand: ${error.response?.data?.message || error.message}`);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this brand?')) {
            return;
        }
        try {
            await brandAPI.delete(id);
            navigate('/brands');
        } catch (error) {
            setError(formatErrorResponse(error));
        }
    };

    if (permissionsLoading) {
        return (
            <Container className="py-3">
                <div className="text-center">Loading...</div>
            </Container>
        )
    }

    if (!hasPermission('stock.items.modify')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to update brands.
                </Alert>
            </Container>
        )
    }

    return (
        <Container>
            <Row className="justify-content-left">
                <Col md={8} lg={6}>
                    <h1>Update Brand</h1>
                    <Form onSubmit={submitHandler}>
                        <Form.Group className="mb-4" controlId="name">
                            <Form.Label>Name*</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter name"
                                value={brand.name}
                                onChange={(e) => setBrand({ ...brand, name: e.target.value })}
                                disabled={!brand.is_active}
                                style={{ opacity: brand.is_active ? 1 : 0.6 }}
                                required
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>
                        {error && (
                            <Alert variant="danger" onClose={() => setError(null)} dismissible>
                                {error}
                            </Alert>
                        )}
                        <Row className="mt-3">
                            <Col>
                                {brand.is_active ? (
                                    <>
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            className="me-2"
                                        >
                                            Update
                                        </Button>
                                        <Button
                                            variant="danger"
                                            onClick={handleToggleActive}
                                            className="me-2"
                                        >
                                            Deactivate
                                        </Button>
                                        <Button
                                            variant="outline-primary"
                                            onClick={() => navigate('/brands')}
                                        >
                                            <i className="fas fa-arrow-left me-2"></i>
                                            Back to List
                                        </Button>
                                    </>
                                ) : (
                                    <Button
                                        variant="success"
                                        onClick={handleToggleActive}
                                    >
                                        Activate
                                    </Button>
                                )}
                            </Col>
                        </Row>

                        {!brand.is_active && (
                            <Alert variant="warning" className="mt-3">
                                This brand is currently inactive. Activate it to make changes.
                            </Alert>
                        )}
                    </Form>
                </Col>
            </Row>
        </Container>
    )
}

export default BrandUpdateScreen
